import { X } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import {
    ChallengePageEntrySupportDto,
    ChallengeStatus
} from '@api/ChallengeClient';
import { Badge, BadgeDot } from '@components/badge';
import { DatePicker } from '@components/date-picker';
import { Filters } from '@components/filters';
import { PageHeader } from '@components/page-header';
import { SortingRule, TableComponent } from '@components/table';
import { Button } from '@components/ui/button';
import { getCdnImage } from '@components/utils/cdn-image';
import { I18n } from '@core/i18n';
import { links } from '@core/router';
import { injectTSDI } from '@core/tsdi';
import { getBadgeType } from '@src/communities/community/challenges';
import { ChallengeDeleteModal } from '@src/communities/community/challenges/modals/challenge-delete-modal';
import { useChallengesStore } from './store';

const STATUS_OPTIONS = [
    { label: 'Draft', value: ChallengeStatus.DRAFT },
    { label: 'Scheduled', value: ChallengeStatus.SCHEDULED },
    { label: 'Published', value: ChallengeStatus.PUBLISHED },
    { label: 'In progress', value: ChallengeStatus.IN_PROGRESS },
    { label: 'Finished', value: ChallengeStatus.FINISHED },
    { label: 'Canceled', value: ChallengeStatus.CANCELED }
].map((option) => ({
    ...option,
    adornment: <BadgeDot type={getBadgeType(option.value)} />
}));

const toDate = (value?: string) => (value ? new Date(value) : null);

const toDayStart = (value: Date | null) =>
    value ? `${formatDay(value)}T00:00:00Z` : undefined;

const toDayEnd = (value: Date | null) =>
    value ? `${formatDay(value)}T23:59:59Z` : undefined;

function formatDay(value: Date) {
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
}

export const Challenges = () => {
    const navigate = useNavigate();
    const { formatLongDate } = injectTSDI(I18n);
    const {
        challenges,
        statusFilter,
        setStatusFilter,
        dateRange,
        setDateRange,
        sorting,
        setSorting,
        setSearchTerm,
        dispatch,
        refresh
    } = useChallengesStore();

    const deleteDialogOpenState = React.useState(false);
    const [, setDeleteDialogOpen] = deleteDialogOpenState;
    const [selectedChallenge, setSelectedChallenge] =
        React.useState<ChallengePageEntrySupportDto | null>(null);

    const onSortingChange = (sorting?: SortingRule) => {
        if (sorting) {
            setSorting(sorting);
        }
    };

    return (
        <>
            <PageHeader title="Challenges" />

            <div className="mb-4 flex flex-wrap items-end gap-3">
                <div className="w-[220px]">
                    <Filters<ChallengeStatus, true>
                        multiple
                        label="Status"
                        value={statusFilter}
                        options={STATUS_OPTIONS}
                        onChange={(value) => setStatusFilter(value)}
                    />
                </div>
                <div className="w-[180px]">
                    <DatePicker
                        label="From"
                        clearable
                        value={toDate(dateRange.dateFrom)}
                        onChange={(value) =>
                            setDateRange({
                                ...dateRange,
                                dateFrom: toDayStart(value)
                            })
                        }
                    />
                </div>
                <div className="w-[180px]">
                    <DatePicker
                        label="To"
                        clearable
                        value={toDate(dateRange.dateTo)}
                        onChange={(value) =>
                            setDateRange({
                                ...dateRange,
                                dateTo: toDayEnd(value)
                            })
                        }
                    />
                </div>
                <Button
                    variant="ghost"
                    onClick={() => {
                        setStatusFilter([]);
                        setDateRange({});
                    }}
                >
                    <X />
                    Clear
                </Button>
            </div>

            <ChallengeDeleteModal
                openState={deleteDialogOpenState}
                challengeId={selectedChallenge?.id}
                challengeName={selectedChallenge?.presentation.name.en}
                onSuccess={() => {
                    setDeleteDialogOpen(false);
                    setSelectedChallenge(null);
                    refresh();
                }}
            />
            <TableComponent<ChallengePageEntrySupportDto[]>
                items={challenges}
                columns={[
                    {
                        Header: '',
                        accessor: 'presentation',
                        style: { width: 60 },
                        disableSortBy: true,
                        Cell: ({
                            row: {
                                original: { presentation }
                            }
                        }) =>
                            presentation?.photo ? (
                                <img
                                    className="size-10 rounded-md object-cover"
                                    src={getCdnImage(
                                        presentation.photo.bucketName,
                                        presentation.photo.objectKey,
                                        {
                                            resize: {
                                                width: 50,
                                                height: 50
                                            }
                                        }
                                    )}
                                />
                            ) : null
                    },
                    {
                        Header: 'Name',
                        id: 'name',
                        disableSortBy: true,
                        accessor: (challenge) => (
                            <div className="flex flex-col">
                                <Link
                                    className="font-medium text-primary hover:underline"
                                    to={links.communityChallengeEditForm(
                                        challenge.community.id.toString(),
                                        challenge.id.toString()
                                    )}
                                >
                                    {challenge.presentation.name.en}
                                </Link>
                                <span className="text-sm text-muted-foreground">
                                    {challenge.dateTimeRange &&
                                        `${formatLongDate(
                                            challenge.dateTimeRange.from
                                        )} – ${formatLongDate(
                                            challenge.dateTimeRange.to
                                        )}`}
                                </span>
                            </div>
                        )
                    },
                    {
                        Header: 'Community',
                        id: 'community',
                        style: { width: 220 },
                        disableSortBy: true,
                        accessor: (challenge) => (
                            <Link
                                className="text-primary hover:underline"
                                to={links.communityDetails(
                                    challenge.community.id.toString()
                                )}
                            >
                                {challenge.community.name}
                            </Link>
                        )
                    },
                    {
                        Header: 'Type',
                        id: 'type',
                        style: { width: 110 },
                        disableSortBy: true,
                        accessor: (challenge) =>
                            challenge.teamsLeaderboardEnabled
                                ? 'Team'
                                : 'Individual'
                    },
                    {
                        Header: 'Status',
                        id: 'status',
                        style: { width: 150 },
                        disableSortBy: true,
                        accessor: (challenge) => (
                            <Badge type={getBadgeType(challenge?.status)}>
                                {challenge?.status ?? ''}
                            </Badge>
                        )
                    }
                ]}
                contextMenu={[
                    {
                        label: 'Leaderboard',
                        hidden: (challenge) => !challenge.leaderboardEditable,
                        action: (challenge) =>
                            navigate(
                                links.challengeLeaderboard(
                                    challenge.id.toString()
                                )
                            )
                    },
                    {
                        label: 'Edit',
                        action: (challenge) =>
                            navigate(
                                links.communityChallengeEditForm(
                                    challenge.community.id.toString(),
                                    challenge.id.toString()
                                )
                            )
                    },
                    {
                        label: 'Delete',
                        action: (challenge) => {
                            setSelectedChallenge(challenge);
                            setDeleteDialogOpen(true);
                        }
                    }
                ]}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search by challenge or community name..."
                onChangePage={(page) => dispatch({ type: 'page', page })}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                onSortingChange={onSortingChange}
                sorting={sorting as SortingRule | undefined}
            />
        </>
    );
};

export default Challenges;
